import { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faFileExport, faInfoCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { Api } from 'api';
import { ReduxState, SessionProperty } from 'types';
import * as utils from 'myParkdUtils/utils';
import * as Sentry from '@sentry/react';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import LoadingSpinner from 'components/LoadingSpinner';
import CheckedExportFields, { getAllSessionFields } from 'screens/modals/sessionExport/CheckedExportFields';
import moment from 'moment';
import { GAAction, GACategory, trackDataLayerEvent } from 'myParkdUtils/googleAnalytics';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { formatMessage } from 'i18n/CurrentLocale';
import ErrorMessageBox from 'components/ErrorMessageBox';
import { ColumnFilter } from '@tanstack/react-table';
import { DateRange } from 'react-day-picker';
import DateRangePickerInline from 'components/DateRangePickerInline';
import { differenceInCalendarDays } from 'date-fns';
import { captureMessageWithError } from 'sentryTools';
import { FeatureKey, PARKING_SESSIONS_TAB } from 'myParkdUtils/enums';
import PermissionGate from 'components/PermissionGate';
import { SortedByExportField } from './SortedByExportField';


export type FileFormat = 'PDF' | 'XLSX';
export type SessionExportField = { value: SessionProperty; label: string };

export const DEFAULT_EXPORT_FILETYPE: FileFormat = 'XLSX';
export const EXPORT_TIME_LIMIT_IN_DAYS = 31;


export const downloadFile = (fileStream: any, fileType: FileFormat) => {
    const blob = new Blob([fileStream], {
        type: fileType === 'XLSX' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf',
    });

    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `parking_sessions.${fileType.toLowerCase()}`;
    document.body.appendChild(link);
    link.click();
};

export const ExportParkingHistoryModal = (props: ExportParkingHistoryModalProps) => {

    const { tableFilters, openedTab } = props.modalData;

    const getDefaultSorting = (): SessionProperty => {
        return sessionsType === PARKING_SESSIONS_TAB.TAB_HISTORICAL
            ? props.exportSortingHistoricalSessions?.[0]?.id as SessionProperty
            : props.exportSortingOffstreetSessions?.[0]?.id as SessionProperty;
    }

    const dateFrom = utils.getColumnFilter(tableFilters, 'dateFrom')?.value as Date;
    const dateTo = utils.getColumnFilter(tableFilters, 'dateTo')?.value as Date;
    const filteredLicensePlate = utils.getColumnFilter(tableFilters, 'licensePlate')?.value as string;

    const [dateRange, setDateRange] = useState<DateRange>({ from: dateFrom, to: dateTo });
    const [sessionsType, setSessionsType] = useState<PARKING_SESSIONS_TAB>(openedTab);
    const [licensePlate, setLicensePlate] = useState<string>(filteredLicensePlate);
    const [checkedFields, setCheckedFields] = useState<SessionExportField[]>(getAllSessionFields(sessionsType));
    const [sorting, setSorting] = useState<SessionProperty>(getDefaultSorting());
    const [selectedFileFormat, setSelectedFileFormat] = useState<FileFormat>(DEFAULT_EXPORT_FILETYPE);
    const [isFetchingFile, setisFetchingFile] = useState<boolean>(false);
    const [isFinishedExporting, setIsFinishedExporting] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);

    const exportTimeLimitExceeded = differenceInCalendarDays(dateRange.to!, dateRange.from!) + 1 > EXPORT_TIME_LIMIT_IN_DAYS;

    useEffect(() => {
        const sessionFields = getAllSessionFields(sessionsType);
        setCheckedFields(sessionFields);
        setSorting(getDefaultSorting());
    }, [sessionsType]);

    useEffect(() => {
        const sortByFieldIsChecked = checkedFields.some(field => field.value === sorting);
        if (!sortByFieldIsChecked) {
            const newSorting = checkedFields[0]?.value;
            setSorting(newSorting!);
        }
    }, [checkedFields]);

    const handleLicensePlateFilterChange = (licensePlate: string) => {
        const filteredLicensePlate = utils.stripStringFromNonAlphaNumericCharacters(licensePlate).toUpperCase();
        setLicensePlate(filteredLicensePlate);
    };

    const exportParkingHistory = async () => {
        try {
            setisFetchingFile(true);
            const filtersString = utils.generateFilterQueryParams([
                { id: 'licensePlate', value: licensePlate },
                { id: 'dateFrom', value: moment(dateRange.from).format('YYYY-MM-DD') + 'T00:00:00' },
                { id: 'dateTo', value: moment(dateRange.to).format('YYYY-MM-DD') + 'T23:59:59' },
            ]);
            const sortingString = `sort=${sorting},ASC`;
            const typeString = `&reportType=${selectedFileFormat}`;
            const fieldsString = utils.generateFieldsQueryParams(checkedFields);
            const fileStream: any = sessionsType === PARKING_SESSIONS_TAB.TAB_HISTORICAL
                ? await Api.getSessionsExportOnstreet(props.token!, `${filtersString}${sortingString}${typeString}${fieldsString}`)
                : await Api.getSessionsExportOffstreet(props.token!, `${filtersString}${sortingString}${typeString}${fieldsString}`)

            downloadFile(fileStream, selectedFileFormat);
        } catch (error) {
            console.error('error', error);
            Sentry.setContext('user', {
                userId: props.user?.id,
                givenName: props.user?.givenName,
                familyName: props.user?.familyName,
                email: props.user?.email,
            });
            captureMessageWithError('Session export failed', error);
            setHasError(true);
        } finally {
            setIsFinishedExporting(true);
            setisFetchingFile(false);
            trackDataLayerEvent({ event: GAAction.PARKING_SESSIONS_EXPORT });
        }
    };

    const InfoPanel = () => (
        <div style={{
            padding: '0.25em',
            color: '#777',
            fontSize: '14px',
            textAlign: 'center'
        }}>
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.5em' }} />
            <FormattedMessage id="PARKING_HISTORY_MODAL_WARNING_MAX_DAYS" />
        </div>
    );

    return (
        <div id="export-history-modal" className="panel large radius-rectangle">
            <div className="panel-header">
                <h2 className="bold-title">
                    <FontAwesomeIcon icon={faFileExport} />
                    {!isFetchingFile && !isFinishedExporting && <FormattedMessage id="PARKING_HISTORY_MODAL_EXPORT_PARKING_SESSIONS" />}
                    {isFetchingFile && <FormattedMessage id="PARKING_HISTORY_MODAL_GENERATING_FILE" />}
                    {isFinishedExporting && <FormattedMessage id="PARKING_HISTORY_MODAL_EXPORT_COMPLETED" />}
                </h2>
                <button className="btn-close-modal" onClick={() => props.closeModal()} />
            </div>

            <div className="panel-content">
                {isFetchingFile || isFinishedExporting || hasError ? (
                    <Fragment>
                        {isFetchingFile && (
                            <>
                                <LoadingSpinner message={<FormattedMessage id="PARKING_HISTORY_MODAL_EXPORTING_SESSIONS" />} />
                            </>
                        )}

                        {isFinishedExporting && (
                            <Fragment>
                                {hasError ? (
                                    <p>
                                        <FontAwesomeIcon
                                            icon={faTimesCircle}
                                            color="#ff4500"
                                            size={'lg'}
                                            style={{ marginRight: '0.4em' }}
                                        />
                                        <FormattedMessage id="ERROR_SOMETHING_WENT_WRONG_TRY_AGAIN_LATER" />
                                    </p>
                                ) : (
                                    <p>
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            color="#35D38C"
                                            size={'lg'}
                                            style={{ marginRight: '0.4em' }}
                                        />
                                        <FormattedMessage id="YOUR_EXPORT_HAS_COMPLETED_SUCCESSFULLY" />
                                    </p>
                                )}
                                <button className="btn-blue" onClick={() => props.closeModal()}>
                                    <FormattedMessage id="GLOBAL_CLOSE" />
                                </button>
                            </Fragment>
                        )}
                    </Fragment>
                ) : (
                    <Fragment>
                        <div className="modal-content">

                            <div className="export-modal-section">
                                <div className='flex-space-between'>
                                    <label style={{ marginRight: '1em' }}>
                                        <FormattedMessage id="PARKING_HISTORY_MODAL_LABEL_PERIOD" />
                                    </label>
                                    <DateRangePickerInline
                                        startDate={dateRange.from!}
                                        endDate={dateRange.to!}
                                        numberOfMonths={1}
                                        onDateRangeChanged={(dateRange) => setDateRange(dateRange)}
                                        bottomComponent={<InfoPanel />}
                                    />
                                </div>
                                {exportTimeLimitExceeded &&
                                    <ErrorMessageBox
                                        errorMessage={formatMessage({ id: 'PARKING_HISTORY_MODAL_ERROR_MAX_DAYS' })}
                                        style={{ margin: '10px 0 0 0' }}
                                    />
                                }
                            </div>

                            <PermissionGate featureKey={FeatureKey.PARKD_CARDS}>
                                <div className="export-modal-section">
                                    <div className='flex-2-col-parent'>
                                        <div className='flex-2-col'>
                                            <label htmlFor="export-parking-type">
                                                <FormattedMessage id="PARKING_HISTORY_MODAL_LABEL_PARKING_TYPE" />
                                            </label>
                                        </div>
                                        <div className='flex-2-col'>
                                            <div className="select-wrap">
                                                <select id="export-parking-type" data-testid='export-parking-type'
                                                    value={sessionsType}
                                                    onChange={(event) => setSessionsType(event.target.value as PARKING_SESSIONS_TAB)}
                                                >
                                                    <option value={PARKING_SESSIONS_TAB.TAB_HISTORICAL}>
                                                        <FormattedMessage id="OPTION_ONSTREET_SESSIONS" />
                                                    </option>
                                                    <option value={PARKING_SESSIONS_TAB.TAB_OFFSTREET}>
                                                        <FormattedMessage id="OPTION_OFFSTREET_SESSIONS" />
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </PermissionGate>

                            <div className="export-modal-section">
                                <div className='flex-2-col-parent'>
                                    <div className='flex-2-col'>
                                        <label htmlFor="export-license-plate">
                                            <FormattedMessage id="PARKING_HISTORY_MODAL_LABEL_LICENSE_PLATE" />
                                        </label>
                                    </div>
                                    <div className='flex-2-col'>
                                        <div className='full-width-form'>
                                            <input
                                                id="export-license-plate"
                                                type='text'
                                                value={licensePlate}
                                                onChange={(e) => handleLicensePlateFilterChange(e.target.value)}
                                                maxLength={10}
                                                className='text-field'
                                                placeholder='1ABC123'
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <CheckedExportFields
                                checkedFields={checkedFields}
                                setCheckedFields={setCheckedFields}
                                sessionsType={sessionsType}
                            />

                            <SortedByExportField
                                checkedFields={checkedFields}
                                sorting={sorting}
                                setSorting={setSorting}
                            />

                            <div className="export-modal-section">
                                <div className='flex-2-col-parent'>
                                    <div className='flex-2-col'>
                                        <label htmlFor="export-format">
                                            <FormattedMessage id="PARKING_HISTORY_MODAL_CHOOSE_FILE_FORMAT" />
                                        </label>
                                    </div>
                                    <div className='flex-2-col'>
                                        <div className="select-wrap">
                                            <select
                                                id="export-format"
                                                value={selectedFileFormat}
                                                onChange={(e) => setSelectedFileFormat(e.target.value as FileFormat)}
                                            >
                                                <option value="XLSX">XLSX</option>
                                                <option value="PDF">PDF</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button disabled={exportTimeLimitExceeded} className="btn-blue full-width" onClick={() => exportParkingHistory()}>
                                <FontAwesomeIcon icon={faFileExport} className="fa-icon" />
                                <FormattedMessage id="GLOBAL_EXPORT" />
                            </button>
                        </div>
                    </Fragment>
                )}
            </div>
        </div>
    );
};

interface OwnProps {
    modalData: {
        tableFilters: ColumnFilter[],
        openedTab: PARKING_SESSIONS_TAB
    }
}
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
export type ExportParkingHistoryModalProps = StateProps & DispatchProps & OwnProps;

/* istanbul ignore next */
function mapStateToProps(state: ReduxState) {
    return {
        token: state.auth?.token,
        user: state.user?.user,
        userLocale: state.user?.user?.locale,
        exportSortingHistoricalSessions: state.historicalSessions?.exportSorting,
        exportSortingOffstreetSessions: state.offstreetSessions?.exportSorting
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        closeModal: modalWindowActions.closeModal,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ExportParkingHistoryModal);